import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { selectAppState } from "../../../app/slice/appSlice";
import { useAppSelector } from "../../../app/hooks";
import { Divider, Flex, ListItem, UnorderedList, useMediaQuery } from "@chakra-ui/react";
import { Icons, ToolTip, Typography, useCurrencyFormatter } from "@reconvert/react-ui-component";
import { Slot, Widget } from "../../../models";
import { getDiscountText, removeHtmlElementsFromString } from "../../../utils/helpers";
import { getProductToDisplayVerbose } from "../../../utils/getConditionText";
import SpecificProductBlock from "./SpecificProductBlock";
import useUnsavedBar from "../../../hooks/useUnsavedBar";
import useVariants from "../../../hooks/useVariants";
import { PageType } from "@reconvert/reconvert-utils";
import { useCentralConfirmModal } from "../../../context/ConfirmModalContext";

const WidgetView = ({ widgets, variantStatus, variantId, slot }: { widgets: Widget[]; variantStatus: boolean, variantId: string, slot: Slot }) => {
  const [isLargerThan1284] = useMediaQuery("(min-width: 1284px)");

  const {
    view: { isWidgetView },
  } = useAppSelector(selectAppState);

  if (!isWidgetView) return null;

  return (
    <>
      <Divider />
      {widgets.length === 0 ? (
        <NoWidgetsMessage variantStatus={variantStatus} />
      ) : (
          <WidgetList widgets={widgets} variantId={variantId} slot={slot} isLargerThan1284={isLargerThan1284} variantStatus={variantStatus} />
      )}
    </>
  );
};

const NoWidgetsMessage = ({ variantStatus }: { variantStatus: boolean }) => {
  const { t } = useTranslation();

  return (
    <Typography
      my={3}
      textAlign="center"
      variant="subheading2"
      color={variantStatus ? "neutrals.600" : "disable.color"}
    >
      {t("No widgets will be shown on this slot")}
    </Typography>
  );
};

const WidgetList = ({
  widgets,
  isLargerThan1284,
  variantStatus,
  variantId,
  slot
}: {
  widgets: Widget[];
  isLargerThan1284: boolean;
  variantStatus: boolean;
  variantId:string;
  slot: Slot 
}) => {
  const { t } = useTranslation();
  const filteredWidgets = widgets?.filter(({ parentWidgetId, type }) => !parentWidgetId && type !== "DIVIDER");

  const { preventAction } = useUnsavedBar();
  const { navigateToEditor, handleDeleteVariantId, variants } = useVariants();
  const { isMobile } = useAppSelector(selectAppState);

  const { openConfirmModal } = useCentralConfirmModal();

  const variantName = variants?.find((v: any) => v.id === variantId)?.name ?? "";

  const handleOpenDeleteModal = useCallback(() => {
    openConfirmModal({
      title: t("Are you sure you want to delete {{name}}?", { name: t(variantName) }),
      okButtonText: t("Delete widgets"),
      cancelButtonText: t("Cancel"),
      onConfirm: () => handleDeleteVariantId(variantId),
      onCancel: () => {},
      destructive: true,
    });
  }, [openConfirmModal, t, variantName, handleDeleteVariantId, variantId]);

  return (
    <>
      <Flex justifyContent={"space-between"} alignItems={"center"} mt={"12px"} mb={"8px"}>
        <Typography color={variantStatus ? "black" : "disable.color"} fontWeight={500} variant="subheading2">
          {t("Widgets")}
        </Typography>
        <Flex gap={2} alignItems={"center"}>
          <Typography onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            preventAction(() => navigateToEditor(variantId, slot.id, slot.slotNo))
          }} color={"#2F73DA"} size={"14px"} variant="subheading1">
            {t("Edit")}
          </Typography>
          {isMobile && <Icons.TrashSimple color="error.700" height={"16px"} width={"16px"} cursor={"pointer"} onClick={() => preventAction(handleOpenDeleteModal)} />}
        </Flex>
      </Flex>
      <UnorderedList
        styleType="none"
        sx={{
          display: "grid",
          gridTemplateColumns: isLargerThan1284 ? "1fr 1fr" : "1fr",
          gap: "4px",
          marginLeft: "6px",
        }}
      >
        {filteredWidgets.map((widget, index) => (
          <ToolTip key={index} trigger={isMobile ? "click" : "hover"} height="100%" placement="top" label={<WidgetDetail widgetInfo={widget} />}>
            <ListItem fontSize="14px" width="fit-content">
              <Typography variant="body"> &#x2022; {t(widget?.name || widget?.type)}</Typography>
            </ListItem>
          </ToolTip>
        ))}
      </UnorderedList>
    </>
  );
};

const WidgetDetail = ({ widgetInfo }: { widgetInfo: Widget }) => {
  const widgetTypeComponents: Record<string, JSX.Element | null> = {
    TEXT: <WidgetInformation title="Text" text={widgetInfo?.text} />,
    BANNER: <WidgetInformation title="Banner" text={widgetInfo?.content?.title} />,
    TIMER: <TimerWidget widgetInfo={widgetInfo} />,
    MULTI_PRODUCT_UPSELL: <CommonWidget widgetInfo={widgetInfo} />,
    RECOMMENDATIONS: <CommonWidget widgetInfo={widgetInfo} />,
    BUNDLE: <CommonWidget widgetInfo={widgetInfo} />,
    UPSELL: <UpsellBlock widget={widgetInfo} />,
    DISCOUNT_TIMER: <DiscountTimerWidget widgetInfo={widgetInfo} />,
  };

  return widgetTypeComponents[widgetInfo.type] || null;
};

const DiscountTimerWidget = ({ widgetInfo }: { widgetInfo: any }) => {
  const { t } = useTranslation();
  const { activePage } = useAppSelector(selectAppState);

  let timerObject = widgetInfo?.duration;

  if (activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE) {
    timerObject = widgetInfo?.duration;
  }

  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = timerObject || {};
  const timerDurationStr = `${days} ${t("days")}, ${hours} ${t("hours")}, ${minutes} ${t("minutes")}, ${seconds} ${t("seconds")}`;

  const formatDate = (dateTimeStr: string) => {
    try {
      const date = new Date(dateTimeStr);

      return date
        .toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(",", " |");
    } catch (error) {
      return dateTimeStr;
    }
  };

  return (
    <>
      <Flex flexDirection="column" gap="2px">
        <Typography variant="small">{t("Discount Timer")}</Typography>
        {widgetInfo?.count === "TO_SPECIFIC_TIME_AND_DATE" ? (
          <Typography variant="xsmall">- {t("Date")}: {formatDate(widgetInfo?.dateAndTime)}</Typography>
        ) : (
          timerDurationStr && <Typography variant="xsmall">- {t("Duration")}: {timerDurationStr}</Typography>
        )}
      </Flex>
    </>
  );
};

const CommonWidget = ({ widgetInfo }: { widgetInfo: Widget }) => (
  <Flex flexDirection="column">
    <Typography variant="body">{widgetInfo?.name}</Typography>
    {widgetInfo?.widgets?.map((widget, index) => <WidgetTypeRenderer widget={widget} />)}
  </Flex>
);

const TimerWidget = ({ widgetInfo }: { widgetInfo: Widget }) => {
  const { t } = useTranslation();
  const { activePage } = useAppSelector(selectAppState);

  let timerObject = widgetInfo?.upsellInfo?.settings?.duration;

  if(activePage === PageType.CHECKOUT || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE || activePage === PageType.PRE_PURCHASE_PRODUCT_PAGE || activePage === PageType.PRE_PURCHASE_CART_DRAWER_PAGE || activePage === PageType.PRE_PURCHASE_CART_PAGE) {
    timerObject = widgetInfo?.upsellInfo?.duration?.duration;
  }
  
  const { days, hours, minutes, seconds } = timerObject;
  const timerDurationStr = `${days} ${t("days")}, ${hours} ${t("hours")}, ${minutes} ${t("minutes")}, ${seconds} ${t("seconds")}`;

    // Format the date string if available
    const formatDate = (dateTimeStr: string) => {
      try {
        const date = new Date(dateTimeStr);
        
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(',', ' |');
      } catch (error) {
        return dateTimeStr; // Fallback to original format if parsing fails
      }
    };

  return (
    <>
      <Flex flexDirection="column" gap="2px">
        <Typography variant="small">{t("Timer")}</Typography>
        {widgetInfo?.upsellInfo?.duration?.count === "TO_SPECIFIC_TIME_AND_DATE" ? <Typography variant="xsmall">- {t("Date")}: {formatDate(widgetInfo?.upsellInfo?.duration?.dateAndTime)}</Typography> : timerDurationStr && <Typography variant="xsmall">- {t("Duration")}: {timerDurationStr}</Typography>}
      </Flex>
    </>
  );
};

const WidgetTypeRenderer = ({ widget }: { widget: Widget }) => {
  const widgetComponents: Record<string, JSX.Element | null> = {
    TEXT: <WidgetInformation title="Text" text={widget?.text} />,
    BANNER: <WidgetInformation title="Banner" text={widget?.content?.title} />,
    UPSELL: <UpsellBlock widget={widget} />,
  };

  return widgetComponents[widget.type] || null;
};

const UpsellBlock = ({ widget }: { widget: Widget }) => {
  const { t } = useTranslation();

  return (
    <UnorderedList pl="5px" width="100%">
      <ListItem>
        <WidgetInformation title="Product" />
        {widget?.upsellInfo?.type && (
          <Typography variant="xsmall">
            {widget?.upsellInfo?.type === "SPECIFIC_PRODUCT" ? (
              <SpecificProductBlock specificProduct={widget?.upsellInfo?.settings?.SPECIFIC_PRODUCT} isListItemView />
            ) : (
              <>- {t(getProductToDisplayVerbose(widget?.upsellInfo?.type)!)}</>
            )}
          </Typography>
        )}
        {widget?.blocks?.some(block => block?.type === "PRICE_AND_DISCOUNT") && <PriceAndDiscountBlock widget={widget} />}
      </ListItem>
    </UnorderedList>
  );
};

const PriceAndDiscountBlock = ({ widget }: { widget: Widget }) => {
  const { t } = useTranslation();
  const { storeInfo } = useAppSelector(selectAppState);
  const discount = widget?.blocks?.find(block => block?.type === "PRICE_AND_DISCOUNT")?.discount;
  const isSubscriptionEnabled = widget?.blocks?.find(block => block?.type === "SUBSCRIPTION")?.isSubscriptionEnabled;
  const { currencyFormatter } = useCurrencyFormatter();

  return (
    <Typography variant="xsmall">
      - {getDiscountText(t, discount?.type === 'PERCENTAGE' ? discount?.discountValue : currencyFormatter(discount?.discountValue, storeInfo?.currency!), discount?.type, isSubscriptionEnabled)}
    </Typography>
  );
};

const WidgetInformation = ({ title, text }: { title: string; text?: string }) => {
  const { t } = useTranslation();

  return (
    <Flex flexDirection="column" gap="2px">
      <Typography variant="small">{t(title)}</Typography>
      {text && <Typography variant="xsmall">- {removeHtmlElementsFromString(text)}</Typography>}
    </Flex>
  );
};

export default WidgetView;
